import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Tooltip } from 'antd';
import ModalDlg from 'components/Basic/ModalDlg';
import { Close, ExpandMore, ExpandLess, EmojiEvents, CheckCircle, Info, GetApp } from '@material-ui/icons';
import Loading from 'components/Basic/Loading';
import toast from 'components/Basic/Toast';
import { useLazyGetPrizeDrawDetailQuery } from 'core/worldCup/prizeDraws/PrizeDrawsService';
import { useLazyDownloadRaffleRoundsReportQuery } from 'core/worldCup/reports/WorldCupReportsService';
import { PrizeDrawDetail, DetailItem, DetailEntry, EntryStatus } from 'core/worldCup/prizeDraws/types';

type PrizeDrawsDetailModalProps = {
  isOpenModal: boolean;
  prizeDrawId: number | null;
  onCloseModal: () => void;
};

const STATUS_ORDER: EntryStatus[] = ['SELECTED', 'WINNER', 'BACKUP', 'INVALID'];

const PrizeDrawsDetailModal: React.FC<PrizeDrawsDetailModalProps> = ({
  isOpenModal,
  prizeDrawId,
  onCloseModal,
}) => {
  const intl = useIntl();
  const [detail, setDetail] = useState<PrizeDrawDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [expandedRounds, setExpandedRounds] = useState<Record<string, boolean>>({});
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [getPrizeDrawDetail] = useLazyGetPrizeDrawDetailQuery();
  const [downloadRaffleRoundsReport] = useLazyDownloadRaffleRoundsReportQuery();

  useEffect(() => {
    if (isOpenModal && prizeDrawId) {
      loadDetail(prizeDrawId);
    } else {
      setDetail(null);
      setExpandedRounds({});
      setSelectedItemIndex(0);
    }
  }, [isOpenModal, prizeDrawId]);

  const loadDetail = async (id: number) => {
    setIsLoading(true);
    try {
      const res = await getPrizeDrawDetail(id).unwrap();
      setDetail(res);
      // Expandir la primera ronda de cada item por defecto
      const defaults: Record<string, boolean> = {};
      res.items?.forEach((item: DetailItem) => {
        item.rounds?.forEach(round => {
          round.attempts?.forEach(attempt => {
            const key = `${item.prizePoolItemId}-${round.roundNumber}-${attempt.attemptNumber}`;
            defaults[key] = true;
          });
        });
      });
      setExpandedRounds(defaults);
    } catch (e) {
      console.error('Error loading prize draw detail:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRound = (key: string) => {
    setExpandedRounds(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownloadWinners = async () => {
    if (!prizeDrawId) return;
    setIsDownloading(true);
    try {
      const blob = await downloadRaffleRoundsReport({ raffleConfigId: prizeDrawId, verbose: 'es' }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `raffle-rounds-${prizeDrawId}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success({ title: intl.formatMessage({ id: 'Winners report downloaded successfully' }) });
    } catch (e: any) {
      const code = e?.data?.code;
      if (code === 'ReportsRaffleConfigNotFound') {
        toast.error({ title: intl.formatMessage({ id: 'Raffle config not found' }) });
      } else {
        toast.error({ title: intl.formatMessage({ id: 'Error downloading winners report' }) });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const renderEntry = (entry: DetailEntry, idx: number, requiresVisa: boolean) => (
    <div className={`winner_item status_${entry.status.toLowerCase()}`} key={entry.entryId}>
      <span className="position">{idx + 1}</span>
      <div className="participant_info">
        <span className="name">{entry.participant?.fullName}</span>
        <div className="participant_meta">
          {entry.participant?.phone && (
            <span className="participant_detail">{entry.participant.phone}</span>
          )}
          {entry.participant?.participationCode && (
            <span className="participant_detail code">
              {entry.participant.participationCode}
            </span>
          )}
        </div>
        {requiresVisa && (
          <span className={`visa_status ${entry.participant?.hasAmericanVisa ? 'has_visa' : 'no_visa'}`}>
            {intl.formatMessage({ id: entry.participant?.hasAmericanVisa ? 'Has VISA' : 'No VISA' })}
          </span>
        )}
        {entry.notes && <span className="entry_note">{entry.notes}</span>}
      </div>
    </div>
  );

  const renderResultsByRound = (item: DetailItem, roundOffset: number) => {
    if (!item.rounds || item.rounds.length === 0) return null;

    return item.rounds.map(round =>
      round.attempts?.map(attempt => {
        const entriesByStatus: Record<string, DetailEntry[]> = {};
        attempt.entries?.forEach(entry => {
          if (!entriesByStatus[entry.status]) entriesByStatus[entry.status] = [];
          entriesByStatus[entry.status].push(entry);
        });

        const hasEntries = attempt.entries && attempt.entries.length > 0;
        if (!hasEntries) return null;

        const globalRound = roundOffset + round.roundNumber;
        const key = `${item.prizePoolItemId}-${round.roundNumber}-${attempt.attemptNumber}`;
        const isExpanded = !!expandedRounds[key];

        const winnerCount = (entriesByStatus['WINNER'] || []).length + (entriesByStatus['SELECTED'] || []).length;
        const backupCount = (entriesByStatus['BACKUP'] || []).length;
        const invalidCount = (entriesByStatus['INVALID'] || []).length;

        return (
          <div className={`round_block ${isExpanded ? 'expanded' : 'collapsed'}`} key={key}>
            <button className={`round_header ${isExpanded ? 'open' : ''}`} onClick={() => toggleRound(key)}>
              <div className="round_labels">
                <span className="round_pill">
                  <FormattedMessage id="Round" /> {globalRound}
                </span>
                {attempt.attemptNumber > 1 && (
                  <span className="attempt_pill">
                    <FormattedMessage id="Attempt" /> {attempt.attemptNumber}
                  </span>
                )}
              </div>
              <div className="round_summary">
                {winnerCount > 0 && (
                  <span className="summary_chip winners">{winnerCount} <FormattedMessage id="winner_short" defaultMessage="gan." /></span>
                )}
                {backupCount > 0 && (
                  <span className="summary_chip backup">{backupCount} <FormattedMessage id="backup_short" defaultMessage="sup." /></span>
                )}
                {invalidCount > 0 && (
                  <span className="summary_chip invalid">{invalidCount} <FormattedMessage id="invalid_short" defaultMessage="inv." /></span>
                )}
              </div>
              <span className="expand_icon">
                {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </span>
            </button>

            {isExpanded && (
              <div className="round_entries">
                {STATUS_ORDER.map(status => {
                  const entries = entriesByStatus[status];
                  if (!entries || entries.length === 0) return null;
                  return (
                    <div className={`status_group status_${status.toLowerCase()}`} key={status}>
                      <div className="status_label">
                        <span className="status_dot" />
                        <span className="status_text">
                          {intl.formatMessage({ id: `entry_status_${status.toLowerCase()}` })}
                        </span>
                        <span className="status_count">{entries.length}</span>
                      </div>
                      <div className="entries_list">
                        {entries.map((entry, idx) => renderEntry(entry, idx, !!item.reward?.hasAmericanVisa))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })
    );
  };

  const validItems = detail?.items?.filter(item =>
    item.rounds?.some(round => round.attempts?.some(attempt => attempt.entries?.length > 0))
  ) ?? [];

  const activeItem = validItems[selectedItemIndex] ?? null;

  const getItemRoundOffset = (index: number) => {
    let offset = 0;
    for (let i = 0; i < index; i++) {
      const maxRound = validItems[i]?.rounds?.reduce((max, r) => Math.max(max, r.roundNumber), 0) || 0;
      offset += maxRound;
    }
    return offset;
  };

  const getItemWinnerCount = (item: DetailItem) => {
    let count = 0;
    item.rounds?.forEach(round =>
      round.attempts?.forEach(attempt =>
        attempt.entries?.forEach(e => {
          if (e.status === 'WINNER' || e.status === 'SELECTED') count++;
        })
      )
    );
    return count;
  };

  return (
    <ModalDlg className="prizeDraws_detail_modal" visible={isOpenModal} width={860} handleCancel={onCloseModal} isCenter>
      {/* ── Header ── */}
      <div className="pdm_header">
        <div className="pdm_header_icon"><EmojiEvents /></div>
        <div className="pdm_header_title">
          <h3><FormattedMessage id="Prize draw details" /></h3>
          {detail && <span className="pdm_draw_name">{detail.name}</span>}
        </div>
        <div className="pdm_header_actions">
          {detail && detail.status === 'COMPLETED' && (
            <Tooltip title={intl.formatMessage({ id: 'Download winners report' })} placement="bottom">
              <Button
                className="pdm_download_btn"
                onClick={handleDownloadWinners}
                loading={isDownloading}
                icon={!isDownloading ? <GetApp /> : undefined}
              >
                <FormattedMessage id="Download CSV" />
              </Button>
            </Tooltip>
          )}
        </div>
        <div className="pdm_close" onClick={onCloseModal}><Close /></div>
      </div>

      {/* ── Body ── */}
      <div className="pdm_body">
        {isLoading && <Loading visible={isLoading} />}

        {detail && (
          <>
            {/* ── Columna A: info + navegación de premios ── */}
            <div className="pdm_col_a">
              <div className="pdm_meta">
                <div className="pdm_meta_item">
                  <span className="pdm_meta_label"><FormattedMessage id="Status" /></span>
                  <span className={`pdm_status_chip status_${detail.status?.toLowerCase()}`}>
                    {intl.formatMessage({ id: `draw_status_${detail.status?.toLowerCase()}` })}
                  </span>
                </div>
                <div className="pdm_meta_item">
                  <span className="pdm_meta_label"><FormattedMessage id="Main winners" /></span>
                  <span className="pdm_meta_value">{detail.mainWinners}</span>
                </div>
                <div className="pdm_meta_item">
                  <span className="pdm_meta_label"><FormattedMessage id="Alternate winners" /></span>
                  <span className="pdm_meta_value">{detail.backupWinners}</span>
                </div>
              </div>

              <div className="pdm_prizes_nav_title">
                <EmojiEvents fontSize="small" />
                <span><FormattedMessage id="Prizes" /></span>
              </div>

              <div className="pdm_prizes_nav">
                {validItems.map((item, idx) => {
                  const winnerCount = getItemWinnerCount(item);
                  return (
                    <button
                      key={item.prizePoolItemId}
                      className={`pdm_prize_btn ${selectedItemIndex === idx ? 'active' : ''}`}
                      onClick={() => setSelectedItemIndex(idx)}
                    >
                      <span className="pdm_prize_btn_name">{item.reward?.name}</span>
                      <div className="pdm_prize_btn_meta">
                        {winnerCount > 0 && (
                          <span className="pdm_prize_btn_winners">
                            <CheckCircle fontSize="inherit" />
                            {winnerCount}
                          </span>
                        )}
                        {item.reward?.hasAmericanVisa && (
                          <span className="pdm_visa_dot" title={intl.formatMessage({ id: 'Requires VISA' })}>V</span>
                        )}
                      </div>
                    </button>
                  );
                })}
                {validItems.length === 0 && (
                  <div className="pdm_no_prizes">
                    <Info fontSize="small" />
                    <span><FormattedMessage id="No data" defaultMessage="Sin resultados" /></span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Columna B: rondas del premio seleccionado ── */}
            <div className="pdm_col_b">
              {activeItem ? (
                <>
                  <div className="pdm_col_b_header">
                    <span className="pdm_col_b_title">{activeItem.reward?.name}</span>
                    {activeItem.reward?.hasAmericanVisa && (
                      <span className="pdm_visa_badge"><FormattedMessage id="Requires VISA" /></span>
                    )}
                  </div>
                  <div className="pdm_rounds_scroll">
                    <div className="rounds_container">
                      {renderResultsByRound(activeItem, getItemRoundOffset(selectedItemIndex))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="pdm_empty_rounds">
                  <EmojiEvents />
                  <span><FormattedMessage id="Select a prize" defaultMessage="Selecciona un premio" /></span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </ModalDlg>
  );
};

export default PrizeDrawsDetailModal;
